// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Trophy, Zap, Target, Users, Gift, ArrowRight, CheckCircle2, Sparkles, DollarSign, Crown, Medal } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Contest = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contest, setContest] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isCheckingRegistration, setIsCheckingRegistration] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    telegram_handle: '',
    discord_handle: '',
    usdt_trc20_address: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [justRegistered, setJustRegistered] = useState(false);

  // Fetch active contest data
  useEffect(() => {
    const fetchContest = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/contest/active`);
        if (response.ok) {
          const data = await response.json();
          setContest(data);
        }
      } catch (err) {
        console.error('Error fetching contest:', err);
      }
    };

    fetchContest();
  }, []);

  // Check if user is already registered for the contest
  useEffect(() => {
    const checkRegistration = async () => {
      if (user) {
        try {
          const token = localStorage.getItem('access_token');
          const response = await fetch(`${API_BASE_URL}/api/contest/check-registration`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.is_registered) {
              // User is already registered, redirect to submission page
              navigate('/contest/submit');
              return;
            }
          }
        } catch (err) {
          console.error('Error checking registration:', err);
        }
      }
      setIsCheckingRegistration(false);
    };

    checkRegistration();
  }, [user, navigate]);

  // Check if user just registered (has token but success state was lost due to reload)
  useEffect(() => {
    const registered = sessionStorage.getItem('contest_registered');
    if (registered && user) {
      setJustRegistered(true);
      sessionStorage.removeItem('contest_registered');
    }
  }, [user]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.email || !formData.full_name) {
      setError('Email and name are required');
      return;
    }

    if (!formData.usdt_trc20_address) {
      setError('USDT TRC20 wallet address is required for prize payments');
      return;
    }

    // Basic TRC20 address validation (starts with T and is 34 characters)
    if (!formData.usdt_trc20_address.startsWith('T') || formData.usdt_trc20_address.length !== 34) {
      setError('Please enter a valid USDT TRC20 address (starts with T, 34 characters)');
      return;
    }

    if (!formData.password) {
      setError('Password is required to create your InvestVerse account');
      return;
    }

    setIsRegistering(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/contest/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          contest_id: 1
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      // Store the access token to automatically log in the user
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        sessionStorage.setItem('contest_registered', 'true');
        // Reload to trigger AuthProvider to fetch user data
        window.location.reload();
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsRegistering(false);
    }
  };

  // Show loading while checking registration
  if (isCheckingRegistration) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#00ffe7] border-r-transparent mb-4"></div>
          <p className="text-white/70">Loading...</p>
        </div>
      </div>
    );
  }

  if (success || justRegistered) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full">
          {/* Success Animation */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-[#00ffe7] to-emerald-400 mb-6 animate-bounce">
              <CheckCircle2 className="h-12 w-12 text-black" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold mb-4">
              You're In! 🎉
            </h2>
            {user && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 mb-4">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-sm font-medium">Logged in as {user.email}</span>
              </div>
            )}
          </div>

          {/* What's Next */}
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-white/10 p-8 mb-6">
            <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-[#00ffe7]" />
              What Happens Next?
            </h3>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00ffe7]/20 flex items-center justify-center text-[#00ffe7] font-bold border border-[#00ffe7]/30">
                  1
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Test InvestVerse.AI</h4>
                  <p className="text-white/70 text-sm">Explore our AI agents, analyze coins, and discover winning strategies. Spend at least 15-20 minutes trying different features.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00ffe7]/20 flex items-center justify-center text-[#00ffe7] font-bold border border-[#00ffe7]/30">
                  2
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Share Your Honest Feedback</h4>
                  <p className="text-white/70 text-sm">Write a detailed review (500+ words) about what worked, what didn't, and how we can improve. Your honesty helps us build better tools.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00ffe7]/20 flex items-center justify-center text-[#00ffe7] font-bold border border-[#00ffe7]/30">
                  3
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Win Cash Prizes!</h4>
                  <p className="text-white/70 text-sm">
                    Top reviewers win ${contest?.prize_first || 100}, ${contest?.prize_second || 50}, and ${contest?.prize_third || 25}. Plus, the #1 reviewer gets a job opportunity to join our team!
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => navigate('/agent-analysis')}
                className="px-6 py-4 bg-gradient-to-r from-[#00ffe7] to-emerald-400 text-black font-bold rounded-lg hover:scale-105 transition-all duration-300 shadow-lg shadow-[#00ffe7]/30 flex items-center justify-center gap-2"
              >
                <Zap className="h-5 w-5" />
                Start Testing Now
              </button>
              <button
                onClick={() => navigate('/contest/submit')}
                className="px-6 py-4 border-2 border-white/20 text-white font-bold rounded-lg hover:bg-white/10 hover:border-white/40 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Trophy className="h-5 w-5" />
                Submit Review
              </button>
            </div>
          </div>

          {/* Quick Tip */}
          <div className="text-center text-white/60 text-sm">
            💡 <strong>Pro Tip:</strong> Test multiple features and be specific in your review to stand out from other submissions!
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          {/* Animated gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#00ffe7]/10 via-purple-500/5 to-emerald-500/10 animate-gradient-shift"></div>

          {/* Rising particles animation */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-[#00ffe7] rounded-full animate-rise-particle opacity-0"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${8 + Math.random() * 4}s`
                }}
              />
            ))}
          </div>

          {/* Floating victory stars */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute text-yellow-400/20 animate-float-star"
                style={{
                  left: `${10 + Math.random() * 80}%`,
                  top: `${10 + Math.random() * 80}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  animationDuration: `${6 + Math.random() * 3}s`,
                  fontSize: `${12 + Math.random() * 12}px`
                }}
              >
                ⭐
              </div>
            ))}
          </div>

          {/* Glowing orbs */}
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#00ffe7]/5 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-emerald-500/5 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          {/* Prize Highlight Bar */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 text-yellow-400">
              <Crown className="h-4 w-4" />
              <span className="text-sm font-bold">$175 Total Prizes</span>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 border border-purple-500/30 text-purple-400">
              <Sparkles className="h-4 w-4" />
              <span className="text-sm font-bold">Job Opportunity for Winner</span>
            </div>
          </div>

          {/* Main Headline */}
          <div className="text-center mb-12">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-[#00ffe7] via-blue-400 to-purple-400 text-transparent bg-clip-text">
                Get Paid
              </span>
              <br />
              For Your Feedback
            </h1>
            <p className="text-xl sm:text-2xl text-white/80 max-w-3xl mx-auto leading-relaxed">
              Test InvestVerse.AI, share your honest thoughts, and win cash prizes.
              It's that simple.
            </p>
          </div>

          {/* Prize Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {/* 1st Place */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-yellow-500/20 to-orange-500/20 backdrop-blur-lg rounded-2xl border border-yellow-500/30 p-8 text-center">
                <div className="text-6xl mb-4">🥇</div>
                <div className="text-5xl font-extrabold mb-2 bg-gradient-to-r from-yellow-400 to-orange-400 text-transparent bg-clip-text">
                  ${contest?.prize_first || 100}
                </div>
                <div className="text-lg font-semibold text-yellow-400 mb-3">1st Place</div>
                <div className="text-sm text-white/70">
                  + Job Opportunity
                </div>
              </div>
            </div>

            {/* 2nd Place */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-gray-400 to-gray-600 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-gray-500/20 to-gray-600/20 backdrop-blur-lg rounded-2xl border border-gray-500/30 p-8 text-center">
                <div className="text-6xl mb-4">🥈</div>
                <div className="text-5xl font-extrabold mb-2 text-gray-300">
                  ${contest?.prize_second || 50}
                </div>
                <div className="text-lg font-semibold text-gray-400 mb-3">2nd Place</div>
                <div className="text-sm text-white/70">
                  Best insights
                </div>
              </div>
            </div>

            {/* 3rd Place */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-600 to-red-500 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-orange-500/20 to-red-500/20 backdrop-blur-lg rounded-2xl border border-orange-500/30 p-8 text-center">
                <div className="text-6xl mb-4">🥉</div>
                <div className="text-5xl font-extrabold mb-2 text-orange-400">
                  ${contest?.prize_third || 25}
                </div>
                <div className="text-lg font-semibold text-orange-400 mb-3">3rd Place</div>
                <div className="text-sm text-white/70">
                  Detailed feedback
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gradient-to-b from-transparent to-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-center mb-12">
            Three Simple Steps to Win
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#00ffe7]/20 border border-[#00ffe7]/30 mb-4">
                <Zap className="h-8 w-8 text-[#00ffe7]" />
              </div>
              <h3 className="text-xl font-bold mb-2">1. Test the Platform</h3>
              <p className="text-white/70">
                Register, explore our AI agents, analyze crypto coins, and try different features. Take your time.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-500/20 border border-purple-500/30 mb-4">
                <Target className="h-8 w-8 text-purple-400" />
              </div>
              <h3 className="text-xl font-bold mb-2">2. Write Your Review</h3>
              <p className="text-white/70">
                Share what worked, what didn't, and how we can improve. Be honest and detailed (500+ words).
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/30 mb-4">
                <Trophy className="h-8 w-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold mb-2">3. Win Prizes</h3>
              <p className="text-white/70">
                Best reviews win cash! Plus, top reviewer gets invited to join our team.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-white/10 p-8 sm:p-10">
            <h2 className="text-3xl font-extrabold mb-2 text-center">
              Join the Challenge
            </h2>
            <p className="text-white/70 text-center mb-8">
              Create your free account to get started
            </p>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6 flex items-start gap-3">
                <Target className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-200 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold mb-2">Full Name *</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:border-[#00ffe7] text-white placeholder-white/50 transition-colors"
                  placeholder="John Doe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:border-[#00ffe7] text-white placeholder-white/50 transition-colors"
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:border-[#00ffe7] text-white placeholder-white/50 transition-colors"
                  placeholder="Create a secure password"
                  required
                />
                <p className="text-xs text-white/50 mt-1">This creates your InvestVerse.AI account</p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  USDT TRC20 Wallet Address *
                  <span className="text-[#00ffe7] ml-2">💰</span>
                </label>
                <input
                  type="text"
                  name="usdt_trc20_address"
                  value={formData.usdt_trc20_address}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:border-[#00ffe7] text-white placeholder-white/50 transition-colors font-mono text-sm"
                  placeholder="TXxxxxxxxxxxxxxxxxxxxxxxxxxxx (34 characters)"
                  required
                  maxLength={34}
                />
                <p className="text-xs text-white/50 mt-1">
                  Your prize will be paid in USDT on the Tron network (TRC20). Must start with 'T' and be 34 characters.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold mb-2">Telegram (Optional)</label>
                  <input
                    type="text"
                    name="telegram_handle"
                    value={formData.telegram_handle}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:border-[#00ffe7] text-white placeholder-white/50 transition-colors"
                    placeholder="@yourusername"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Discord (Optional)</label>
                  <input
                    type="text"
                    name="discord_handle"
                    value={formData.discord_handle}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:border-[#00ffe7] text-white placeholder-white/50 transition-colors"
                    placeholder="username#1234"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isRegistering}
                className="w-full px-8 py-4 bg-gradient-to-r from-[#00ffe7] to-emerald-400 text-black font-bold text-lg rounded-lg hover:scale-[1.02] transition-all duration-300 shadow-lg shadow-[#00ffe7]/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
              >
                {isRegistering ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin"></div>
                    Creating Account...
                  </>
                ) : (
                  <>
                    Register & Start Testing
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>
            </form>

            <p className="text-center text-white/50 text-xs mt-6">
              By registering, you agree to test the platform and provide honest feedback
            </p>
          </div>
        </div>
      </section>

      {/* What We're Looking For */}
      <section className="py-16 bg-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-center mb-12">
            What Makes a Winning Review?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-start gap-3 mb-3">
                <CheckCircle2 className="h-6 w-6 text-[#00ffe7] flex-shrink-0" />
                <div>
                  <h3 className="font-bold mb-1">Honest & Detailed</h3>
                  <p className="text-white/70 text-sm">
                    Don't hold back. Tell us what's great AND what needs work. We value brutal honesty.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-start gap-3 mb-3">
                <CheckCircle2 className="h-6 w-6 text-[#00ffe7] flex-shrink-0" />
                <div>
                  <h3 className="font-bold mb-1">Specific Examples</h3>
                  <p className="text-white/70 text-sm">
                    "The AI analysis helped me discover BTC's trend" is better than "It's cool."
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-start gap-3 mb-3">
                <CheckCircle2 className="h-6 w-6 text-[#00ffe7] flex-shrink-0" />
                <div>
                  <h3 className="font-bold mb-1">Actionable Suggestions</h3>
                  <p className="text-white/70 text-sm">
                    How can we improve? What features are missing? What confused you?
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-start gap-3 mb-3">
                <Gift className="h-6 w-6 text-purple-400 flex-shrink-0" />
                <div>
                  <h3 className="font-bold mb-1">Bonus: Video Walkthrough</h3>
                  <p className="text-white/70 text-sm">
                    Record a quick video showing your experience. Not required, but gives you an edge!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contest Details */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-8">
            <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Users className="h-6 w-6 text-[#00ffe7]" />
              Contest Details
            </h3>

            <div className="space-y-4 text-white/80">
              {contest && (
                <>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#00ffe7] flex-shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-white">Start Date:</strong> {new Date(contest.start_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#00ffe7] flex-shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-white">Deadline:</strong> {new Date(contest.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                  </div>
                </>
              )}
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-[#00ffe7] flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white">Requirements:</strong> Minimum {contest?.min_word_count || 500} words, test at least 3 features
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-[#00ffe7] flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white">Payment:</strong> USDT (TRC20) sent directly to your wallet
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contest;
